import { AnalyticsFlowProvider } from '@utils/analytics/flow';
import { renderShopifyArtistName } from '@utils/artists';
import { isInNextSixDays } from '@utils/product';
import { Trans, useTranslation } from 'next-i18next';
import { useMemo, useState } from 'react';

import { DEFAULT_CURRENCY_CODE } from '../../../../constants/locations';
import { PreAuthAuctionButton } from '../../../../features/auction/components/PreAuthAuctionButton';
import { DrawByProductId } from '../../../../features/draws/types';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { CurrencyCode } from '../../../../types/Locations';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../../types/Purchasing';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import { ProductPrice } from '../../../ProductPrice/ProductPrice';
import { DialogFlowWithStripe } from '../../Dialog/DialogFlowWithStripe';
import ProductDate from '../../ProductDate';
import { toClientUTCDateFormat } from '../utils';

type Props<T extends ProductPurchaseDetails> = {
  auction: DrawByProductId;
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
};

export const AuctionFlow = ({
  auction,
  product,
  purchasingController,
}: Props<ProductPurchaseDetails>) => {
  const { t } = useTranslation('product');
  const launchDate = new Date(toClientUTCDateFormat(product.launchDate));
  const [showDialog, setShowDialog] = useState(false);
  const preAuthController = usePreAuthDrawCtx();

  const paragraphs = t('auction.default.learnMore.body', {
    returnObjects: true,
  }) as unknown as string[];

  const label = useMemo(() => {
    if (isInNextSixDays(launchDate)) {
      return 'auction.default.subscriptionDate.closingCountdown';
    }
    return 'auction.default.subscriptionDate.closingDate';
  }, [launchDate]);

  return (
    <>
      <div className="flex w-full flex-col gap-6 text-center">
        <div className="flex w-full flex-1 flex-col">
          <p className="text-center font-bold">{t(label)}</p>
          <p className="text-center">
            <ProductDate date={launchDate} />
          </p>
        </div>
        {auction.auctionReservePrice && (
          <div className="flex w-full flex-1 flex-col">
            <p className="text-center text-neutral-4">
              <ProductPrice
                currencyCode={
                  auction.auctionReservePrice.currencyCode as CurrencyCode
                }
                prefix={t('auction.default.reservePrice')}
                price={Number(auction.auctionReservePrice.amount)}
                skipConvertCurrency
                skipTaxCalculation
              />
            </p>
          </div>
        )}
        <div className="flex w-full flex-1 flex-col items-center gap-3">
          {auction && (
            <PreAuthAuctionButton
              auction={auction}
              buttonLocation="mainCta"
              hasCheckoutParam={false}
              product={product}
              size="lg"
            />
          )}
          <button
            className="hyperlink"
            onClick={() => {
              setShowDialog(true);
              // handleHowToClick();
            }}
            type="button"
          >
            {t('auction.default.learnMore.title')}
          </button>
        </div>
      </div>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('auction.default.learnMore.blockChain.title')}
      >
        <div className="flex flex-col gap-3">
          {paragraphs.map((paragraph, index) => (
            <p>
              <Trans
                key={paragraph}
                i18nKey={`auction.default.learnMore.blockChain.body.${index}`}
                values={{ size: auction?.allocation }}
              >
                {paragraph}
              </Trans>
            </p>
          ))}
        </div>
      </SimpleDialog>
      <AnalyticsFlowProvider
        name="draw"
        properties={{
          'type': 'Auction',
          'product title': product.title,
          'artist name': renderShopifyArtistName(
            purchasingController.product?.artist ?? '',
          ),
          'product price':
            product.pricesAndAvailability.pricesAndCurrencies.find(
              (price) => price.currency === DEFAULT_CURRENCY_CODE,
            )?.price || 'unknown',
          'product can be framed': Boolean(
            purchasingController.frameOptions?.length,
          ),
          'product frame upgrade available': Boolean(
            purchasingController.frameOptions,
          ),
          'product frame chosen': Boolean(purchasingController.frame),
          ecommerce: {
            currency: purchasingController.product?.currency ?? '',
            transaction_id: preAuthController.entryIntentResponse.intent?.id,
            value: purchasingController.product?.price ?? '',
            items: [
              {
                item_id: product.id[0].id,
                item_name: product.title,
                item_brand: renderShopifyArtistName(
                  purchasingController.product?.artist ?? '',
                ),
                item_category: product.medium?.join(','),
                price: purchasingController.product?.price ?? '',
                quantity: purchasingController.quantity,
              },
            ],
          },
        }}
      >
        {() =>
          preAuthController.setupSecret && (
            <DialogFlowWithStripe
              clientSecret={preAuthController.setupSecret}
            />
          )
        }
      </AnalyticsFlowProvider>
    </>
  );
};
